import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Megaphone, Send, AlertCircle, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Broadcasts() {
  const { user } = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', priority: 'normal' });
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => { fetchBroadcasts(); }, []);

  const fetchBroadcasts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/broadcasts`, { withCredentials: true });
      setBroadcasts(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSend = async () => {
    if (!form.title || !form.message) return toast.error('Title and message required');
    try {
      const { data } = await axios.post(`${API_URL}/api/broadcasts`, form, { withCredentials: true });
      toast.success(`Broadcast sent to ${data.notified} users`);
      setDialogOpen(false);
      setForm({ title: '', message: '', priority: 'normal' });
      fetchBroadcasts();
    } catch (err) { toast.error('Failed'); }
  };

  const priorityColors = { normal: 'border-border', urgent: 'border-amber-500/30', emergency: 'border-red-500/30' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="broadcasts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <Megaphone className="w-7 h-7 inline mr-3 text-[#FF6F61]" />Broadcasts
            </h1>
            <p className="text-muted-foreground mt-1">{isAdmin ? 'Send alerts to all staff' : 'Organization alerts'}</p>
          </div>
          {isAdmin && <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="new-broadcast-btn"><Send className="w-4 h-4 mr-2" />New Broadcast</Button>}
        </div>

        {broadcasts.length === 0 ? (
          <div className="text-center py-16"><Bell className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No broadcasts</p></div>
        ) : (
          <div className="space-y-3">
            {broadcasts.map(b => (
              <Card key={b.id} className={`bg-card ${priorityColors[b.priority]}`} data-testid={`broadcast-${b.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${b.priority === 'emergency' ? 'bg-red-500/10' : b.priority === 'urgent' ? 'bg-amber-500/10' : 'bg-[#00A6A0]/10'}`}>
                      {b.priority === 'emergency' ? <AlertCircle className="w-5 h-5 text-red-400" /> : <Megaphone className="w-5 h-5 text-[#00A6A0]" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-foreground font-medium">{b.title}</h3>
                        {b.priority !== 'normal' && <span className={`text-[10px] px-2 py-0.5 rounded-full ${b.priority === 'emergency' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>{b.priority}</span>}
                      </div>
                      <p className="text-sm text-muted-foreground">{b.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-2">From {b.sender_name} - {new Date(b.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="glass-card border-border max-w-md">
            <DialogHeader><DialogTitle className="text-foreground">Send Broadcast</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className="text-xs text-muted-foreground">Title</label>
                <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} className="bg-muted border-border text-foreground" data-testid="broadcast-title-input" /></div>
              <div><label className="text-xs text-muted-foreground">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm(p => ({...p, priority: v}))}>
                  <SelectTrigger className="bg-muted border-border text-foreground"><SelectValue /></SelectTrigger>
                  <SelectContent className="glass-card border-border">
                    <SelectItem value="normal">Normal</SelectItem><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><label className="text-xs text-muted-foreground">Message</label>
                <textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[100px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none" data-testid="broadcast-message-input" /></div>
              <Button className="w-full btn-primary" onClick={handleSend} data-testid="send-broadcast-btn">Send to All Staff</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
